/**
 * Whitelist Artists Page - Jerusalem Gold Design
 *
 * Shows all artists in the whitelist in an elegant grid layout.
 * Thumbnails are lazy-loaded from YouTube Music and cached in IndexedDB.
 */

import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { PageHeader } from '@/components/common/PageHeader';
import { useWhitelistStore } from '@/stores/whitelist';
import { useThumbnailStore } from '@/stores/thumbnails';
import { api } from '@/api/client';
import type { ArtistWhitelistEntry } from '@zemer/shared';

// Queue for throttled thumbnail fetching with parallel processing
let fetchQueue: Array<{ artistId: string; resolve: (thumb: string | null) => void }> = [];
let activeRequests = 0;
const MAX_PARALLEL = 5;

async function processFetchQueue(cacheThumbnail: (url: string, data: string) => Promise<void>) {
  while (fetchQueue.length > 0 && activeRequests < MAX_PARALLEL) {
    const item = fetchQueue.shift();
    if (!item) break;

    activeRequests++;

    // Use API client which handles both HTTP and Electron IPC
    api.getArtistThumbnail(item.artistId)
      .then(async (response) => {
        const thumb = response.data?.thumbnail || null;
        if (thumb) {
          // Cache using artistId as key
          await cacheThumbnail(`artist:${item.artistId}`, thumb);
        }
        item.resolve(thumb);
      })
      .catch(() => {
        item.resolve(null);
      })
      .finally(() => {
        activeRequests--;
        processFetchQueue(cacheThumbnail);
      });
  }
}

function queueThumbnailFetch(
  artistId: string,
  hasThumbnail: (url: string) => boolean,
  getThumbnail: (url: string) => string | undefined,
  cacheThumbnail: (url: string, data: string) => Promise<void>
): Promise<string | null> {
  const cacheKey = `artist:${artistId}`;

  // Return cached value immediately
  if (hasThumbnail(cacheKey)) {
    return Promise.resolve(getThumbnail(cacheKey) ?? null);
  }

  return new Promise((resolve) => {
    fetchQueue.push({ artistId, resolve });
    processFetchQueue(cacheThumbnail);
  });
}

export function WhitelistArtistsPage() {
  const navigate = useNavigate();
  const { entries, filterConfig, isLoading, syncWhitelist } = useWhitelistStore();
  const [searchQuery, setSearchQuery] = useState('');

  // Filter entries based on config and search
  const filteredEntries = entries.filter((entry) => {
    // Apply content filters
    if (filterConfig.filtersEnabled) {
      if (!filterConfig.allowFemaleSingers && entry.isFemale) return false;
      if (!filterConfig.allowKidZone && entry.isKidZone) return false;
      if (!filterConfig.allowIsraeli && entry.isIsraeli) return false;
    }

    // Apply search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      return entry.name?.toLowerCase().includes(query) || entry.artistId.toLowerCase().includes(query);
    }

    return true;
  });

  // Sort alphabetically by name
  const sortedEntries = [...filteredEntries].sort((a, b) => {
    const nameA = a.name || a.artistId;
    const nameB = b.name || b.artistId;
    return nameA.localeCompare(nameB);
  });

  const handleArtistClick = (artistId: string) => {
    navigate(`/artist/${artistId}`);
  };

  return (
    <div className="min-h-full page-enter">
      <PageHeader title="Artists" />

      <div className="px-4 pb-8 space-y-6">
        {/* Search with gold accent */}
        <div className="relative">
          <input
            type="text"
            placeholder="Search artists..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="input pr-12"
          />
          <div className="absolute right-4 top-1/2 -translate-y-1/2 text-on-surface-variant">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
              />
            </svg>
          </div>
        </div>

        {/* Stats bar */}
        <div className="flex items-center justify-between">
          <span className="text-sm text-on-surface-variant ">
            <span className="text-primary font-medium">{sortedEntries.length}</span> artists
          </span>
          <button
            className="text-sm text-primary font-medium hover:text-primary-400 transition-colors disabled:opacity-50"
            onClick={syncWhitelist}
            disabled={isLoading}
          >
            {isLoading ? (
              <span className="flex items-center gap-2">
                <span className="w-3 h-3 rounded-full border border-primary border-t-transparent animate-spin" />
                Syncing
              </span>
            ) : (
              'Refresh'
            )}
          </button>
        </div>

        {/* Artist Grid */}
        {isLoading && entries.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 gap-4">
            <div className="w-10 h-10 rounded-full border-2 border-primary border-t-transparent animate-spin" />
            <span className="text-sm text-on-surface-variant">Loading artists...</span>
          </div>
        ) : sortedEntries.length === 0 ? (
          <div className="text-center py-20">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-surface-container flex items-center justify-center">
              <svg className="w-8 h-8 text-on-surface-variant" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
            </div>
            <p className="text-on-surface-variant ">
              {searchQuery ? 'No artists match your search' : 'No artists available'}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 xl:grid-cols-7 gap-3 sm:gap-4">
            {sortedEntries.map((entry, index) => (
              <ArtistCard
                key={entry.artistId}
                entry={entry}
                index={index}
                onClick={() => handleArtistClick(entry.artistId)}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

interface ArtistCardProps {
  entry: ArtistWhitelistEntry;
  index: number;
  onClick: () => void;
}

function ArtistCard({ entry, index, onClick }: ArtistCardProps) {
  const { getThumbnail, cacheThumbnail, hasThumbnail } = useThumbnailStore();
  const cacheKey = `artist:${entry.artistId}`;
  const cachedThumb = getThumbnail(cacheKey);
  const isCached = hasThumbnail(cacheKey);

  const [thumbnail, setLocalThumbnail] = useState<string | null>(cachedThumb ?? null);
  const [loading, setLoading] = useState(!isCached);
  const fetchedRef = useRef(false);

  useEffect(() => {
    // If already cached, use it
    if (isCached && cachedThumb) {
      setLocalThumbnail(cachedThumb);
      setLoading(false);
      return;
    }

    // Skip if already fetching
    if (fetchedRef.current) return;
    fetchedRef.current = true;

    // Fetch and cache
    queueThumbnailFetch(entry.artistId, hasThumbnail, getThumbnail, cacheThumbnail).then((thumb) => {
      setLocalThumbnail(thumb);
      setLoading(false);
    });
  }, [entry.artistId, isCached, cachedThumb, getThumbnail, cacheThumbnail, hasThumbnail]);

  const displayName = entry.name || entry.artistId;

  // Calculate stagger delay (capped at 8 for performance)
  const staggerIndex = Math.min(index % 12, 7);

  return (
    <button
      className="flex flex-col items-center gap-2 p-2 rounded-xl hover:bg-surface-container transition-colors"
      onClick={onClick}
    >
      {/* Avatar */}
      <div className="w-full aspect-square rounded-full overflow-hidden bg-surface-container flex items-center justify-center">
        {loading ? (
          <div className="w-8 h-8 rounded-full border-2 border-primary/30 border-t-primary animate-spin" />
        ) : thumbnail ? (
          <img
            src={thumbnail}
            alt={displayName}
            className="w-full h-full object-cover"
            referrerPolicy="no-referrer"
            crossOrigin="anonymous"
            onError={(e) => {
              (e.target as HTMLImageElement).style.display = 'none';
            }}
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-surface-container-high">
            <span className="text-2xl font-bold text-primary">
              {displayName[0]?.toUpperCase() || '?'}
            </span>
          </div>
        )}
      </div>

      {/* Name */}
      <p className="text-sm font-medium text-center line-clamp-2 w-full">
        {displayName}
      </p>
    </button>
  );
}
